import { createTicketBundle } from '@/lib/tickets/createTicketBundle';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TopBar } from '@/components/layout/TopBar';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllNewJobCatalogRows, newJobCatalogQuery } from '@/lib/catalog/newJobCatalogQuery';
import { createVehicle } from '@/lib/api/vehicleApi';
import { toast } from '@/hooks/use-toast';
import { Loader2, Upload, FileSpreadsheet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CSVTicketVerification } from '@/components/tickets/CSVTicketVerification';
import { CalendarModal } from '@/components/layout/CalendarModal';
import { useDate } from '@/contexts/DateContext';
import { ImportReport, ImportReportData, ImportResult } from '@/components/tickets/ImportReport';
import { parseScheduleCSV } from '@/utils/csvParser';
import { analyzeTicketSplitting } from '@/utils/splitSuggestionRules';
import { normalizeTicketSequences } from '@/utils/sequenceNormalizer';
import { cn } from '@/lib/utils';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { convertDatetimeLocalToUTC } from '@/utils/timezoneMath';
import { getTodayInShopTz } from '@/utils/shopTime';
import { mutateTicketSchedule } from '@/utils/mutateTicketSchedule';
import { clock } from '@/utils/clock';
import { createSpecialtyBayBooking } from '@/utils/specialtyBayBooking';
import { formatShopTime } from '@/utils/formatShopTime';
import { useAuth } from '@/contexts/AuthContext';
import { logTicketCreated } from '@/utils/ticketEventLogger';
import { Checkbox } from '@/components/ui/checkbox';
// Core-100 R4 CORE-Y: row identity (imported once), every row reported, no automatic commitment by default.
import {
  csvImportRowKey,
  classifyImportRowError,
  awaitingAssignmentOutcome,
  classifyImportRowForPlacement,
  notCreatedOutcome,
  CSV_IMPORT_AWAITING_ASSIGNMENT_REASON,
} from '@/lib/import/csvImportRows';
import { ImportPlacementReviewQueue } from '@/components/import/ImportPlacementReviewQueue';
import { applyEligibility, fetchEligibleTechnicianIds } from '@/lib/roles/eligibilityQuery';

interface ParsedTicket {
  ticketNumber: string;
  year?: number;
  make?: string;
  model?: string;
  engineSize?: string;
  customerName: string;
  customerPhone?: string;
  jobs: Array<{
    category: string;
    submenu: string;
    name: string;
    minutes: number;
    customHours?: number;
    notes?: string;
    bay: string;
    skill: number;
  }>;
  assignedTechnician?: string;
  startTime?: string;
  appointmentDate: string;
  appointmentType?: string;
  scheduledTime?: string;
  status: string;
  isWaiter?: boolean;
  // VPV-A2: dropped `vehiclePresent?: boolean` — sister-side cleanup of
  // the CSVTicketVerification dead checkbox. The field was carried on
  // ParsedTicket only to plumb the orphan UI value through; the
  // ticket-insert payload at L525 has never referenced it. DB column
  // is canonically derived by `sync_vehicle_present` trigger from
  // location + timer truth.
  type?: string;
  requiredCategory?: string; // For proficiency-based split assignment
  isComeback?: boolean;
  vin?: string;
}

const ImportJobs = () => {
  const navigate = useNavigate();
  // DCD-A4: dropped `setSelectedDate` destructure — the only call site
  // (L949 post-import) passed `scheduleDate` (Date), but the canonical
  // `scheduleDayLabel` string was already passed alongside via
  // handleImport's signature (L233) and used by every other downstream
  // call (cascade, reassign, normalize). Now routes through the
  // canonical `setSelectedDayLabel` directly. With this round, the
  // workstream-wide `setSelectedDate` consumer count reaches ZERO.
  const { setSelectedDayLabel } = useDate();
  const { shopTimezone } = useShopTimezone();
  const { activeShop, user } = useAuth();
  const [isImporting, setIsImporting] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [progress, setProgress] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedTickets, setParsedTickets] = useState<ParsedTicket[] | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState<Date | null>(null);
  const [ticketsToImport, setTicketsToImport] = useState<ParsedTicket[] | null>(null);
  const [importReport, setImportReport] = useState<ImportReportData | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // OD-40 (founder decision, REVISED 2026-10-15): imported tickets are placed through OptiPlace by the advisor
  // (ImportPlacementReviewQueue) — the former opt-in automatic assignment (smart-assign's #1 placed unseen) is gone.
  const [placementRefresh, setPlacementRefresh] = useState(0);


  const validateFile = (file: File): boolean => {
    // Check file type
    if (file.type !== 'text/csv') {
      toast({
        title: 'Invalid file type',
        description: 'Please select a CSV file',
        variant: 'destructive',
      });
      return false;
    }
    
    // Check file size (max 2MB to prevent timeout)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: 'Please use a CSV file smaller than 2MB. For large imports, split your data into multiple files.',
        variant: 'destructive',
      });
      return false;
    }

    return true;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (validateFile(file)) {
      setSelectedFile(file);
      setParsedTickets(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    
    if (validateFile(file)) {
      setSelectedFile(file);
      setParsedTickets(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleParse = async () => {
    if (!selectedFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a CSV file to parse',
        variant: 'destructive',
      });
      return;
    }

    setIsParsing(true);
    setProgress('Reading CSV file...');

    try {
      const csvText = await selectedFile.text();
      setProgress('Fetching job catalog and technicians...');

      // Fetch job catalog and technicians for matching
      const { data: jobCatalog, error: catalogError } = await fetchAllNewJobCatalogRows<{ label: string; category_key: string; sub_menu: string; minutes_default: number; skill_level: string }>('label, category_key, sub_menu, minutes_default, skill_level', ['label']);
      
      if (catalogError) {
        throw new Error('Failed to fetch job catalog: ' + catalogError.message);
      }

      // WS-ELIG B-R3: the names a CSV line may be matched to are NEW targets
      // for work, so they are THIS shop's roster (it read every accessible
      // shop's) filtered by the one W3 gate — observe: the roster unchanged;
      // enforce: operational people only.
      if (!activeShop?.id) {
        throw new Error('No active shop selected');
      }
      const { data: rosterTechnicians, error: techError } = await supabase
        .from('technicians')
        .select('id, name, employment_status')
        .eq('shop_id', activeShop.id)
        .eq('employment_status', 'Active');

      if (techError) {
        throw new Error('Failed to fetch technicians: ' + techError.message);
      }
      const technicians = applyEligibility(
        rosterTechnicians || [],
        await fetchEligibleTechnicianIds(activeShop.id, []),
      );

      setProgress('Parsing CSV data...');

      // Use in-house parser (no external AI APIs). Pass shop-local "today" as
      // the fallback day label so rows without an appointment date don't
      // silently snap to the browser's UTC date.
      const tickets = await parseScheduleCSV(
        csvText,
        jobCatalog || [],
        technicians || [],
        getTodayInShopTz(shopTimezone),
      );

      if (tickets.length === 0) {
        toast({
          title: 'No tickets found',
          description: 'Could not parse any valid tickets from the CSV file. Please check the format.',
          variant: 'destructive',
        });
        setProgress('');
        return;
      }

      setParsedTickets(tickets);
      toast({
        title: 'CSV Parsed Successfully',
        description: `Found ${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}. Please review and verify the data.`,
      });
      setProgress('');
    } catch (error: any) {
      console.error('Parse error:', error);
      toast({
        title: 'Parse failed',
        description: error.message || 'Failed to parse CSV file',
        variant: 'destructive',
      });
      setProgress('');
    } finally {
      setIsParsing(false);
    }
  };

  const handleImportClick = (tickets: ParsedTicket[]) => {
    setTicketsToImport(tickets);
    setShowCalendar(true);
  };

  const handleDateSelect = (dayLabel: string) => {
    // Parse dayLabel to Date for internal state (noon to avoid boundary issues).
    // dayLabel itself (YYYY-MM-DD shop-local) is the authoritative day key and
    // is threaded through to handleImport so scheduling logic never has to
    // round-trip through Date.toISOString (which shifts day for far-east browsers).
    const [year, month, day] = dayLabel.split('-').map(Number);
    const scheduleDate = new Date(year, month - 1, day, 12, 0, 0);
    setSelectedScheduleDate(scheduleDate);
    setShowCalendar(false);
    setIsAnalyzing(true);
    if (ticketsToImport) {
      handleImport(ticketsToImport, scheduleDate, dayLabel);
    }
  };

  const handleImport = async (tickets: ParsedTicket[], scheduleDate: Date, scheduleDayLabel: string) => {
    setIsImporting(true);
    setProgress('Starting optimized batch import...');
    
    // Helpers to normalize/format suggested times coming from batch assignment
    const normalizeSuggestedStartTime = (input: any, dateStr: string) => {
      if (!input) return `${dateStr}T08:00:00`;
      try {
        const d = new Date(input);
        if (!isNaN(d.getTime())) return d.toISOString();
      } catch { /* best-effort; ignore */ }
      const s = String(input).trim().toLowerCase();
      const m = s.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/);
      let hh = 8, mm = 0;
      if (m) {
        hh = parseInt(m[1], 10);
        mm = m[2] ? parseInt(m[2], 10) : 0;
        const ap = m[3];
        if (ap === 'pm' && hh < 12) hh += 12;
        if (ap === 'am' && hh === 12) hh = 0;
      }
      const iso = `${dateStr}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
      return iso;
    };

    const formatAssignmentTime = (input: any, dateStr: string) => {
      const iso = normalizeSuggestedStartTime(input, dateStr);
      const d = new Date(iso);
      return isNaN(d.getTime()) ? 'TBD' : formatShopTime(iso, shopTimezone, 'short');
    };

    const startTime = Date.now();
    const importResults: ImportResult[] = [];

    try {
      // Use the shop-local dayLabel directly — toISOString().slice(0,10)
      // would silently shift to UTC day for far-east browsers (UTC+12..+14).
      const appointmentDate = scheduleDayLabel;

      // OD-40: only schedulable rows become active work. Completed work stays history; a row missing what placement
      // needs is reported with exactly what is missing. Both are reported, neither is created.
      const ticketsToCreate: ParsedTicket[] = [];
      for (const row of tickets) {
        const placement = classifyImportRowForPlacement(row);
        if (placement.kind === 'schedulable') {
          ticketsToCreate.push(row);
        } else {
          const outcome = notCreatedOutcome(placement);
          importResults.push({ ticketNumber: row.ticketNumber || '(no ticket number)', status: outcome.status, message: outcome.message });
        }
      }


      // PHASE 1: Check for existing tickets (idempotency) and create new ones
      setProgress('Checking for duplicate tickets...');
      
      // SECURITY FIX SEC-003: Use activeShop.id with membership verification instead of arbitrary shop_profile lookup
      if (!activeShop?.id) {
        toast({
          title: 'No Active Shop',
          description: 'Please select a shop before importing tickets.',
          variant: 'destructive',
        });
        setIsImporting(false);
        return;
      }

      // Verify THIS user has an active membership in this shop. The check used to filter only by shop and status, so
      // in any shop with more than one active member (which an owner or manager can read) `.single()` received several
      // rows, PostgREST answered 406, and the import refused everyone with "Access Denied".
      const { data: membershipCheck, error: membershipError } = await supabase
        .from('memberships')
        .select('shop_id')
        .eq('shop_id', activeShop.id)
        .eq('user_id', user?.id ?? '')
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      if (membershipError || !membershipCheck) {
        toast({
          title: 'Access Denied',
          description: 'You do not have access to import tickets for this shop.',
          variant: 'destructive',
        });
        setIsImporting(false);
        return;
      }

      const shopId = activeShop.id;
      
      // Check which tickets already exist on this date
      const ticketNumbers = ticketsToCreate.map(t => t.ticketNumber).filter(Boolean);
      const { data: existingTickets } = await supabase
        .from('tickets')
        .select('ticket_number')
        .eq('shop_id', shopId)
        .eq('scheduled_date', appointmentDate)
        .in('ticket_number', ticketNumbers);
      
      const existingTicketNumbers = new Set(
        (existingTickets || []).map(t => t.ticket_number).filter(Boolean)
      );
      
      // Filter out duplicates
      const ticketsToCreateFiltered = ticketsToCreate.filter(t => 
        !existingTicketNumbers.has(t.ticketNumber)
      );
      
      const skippedCount = ticketsToCreate.length - ticketsToCreateFiltered.length;
      if (skippedCount > 0) {
        console.log(`⚠️ Skipping ${skippedCount} duplicate ticket(s)`);
        importResults.push(...Array.from(existingTicketNumbers).map(ticketNum => ({
          ticketNumber: ticketNum,
          status: 'warning' as const,
          message: 'Already exists on this date (idempotency check)',
        })));
      }
      
      if (ticketsToCreateFiltered.length === 0) {
        const report: ImportReportData = {
          totalTickets: tickets.length,
          successful: 0,
          failed: 0,
          warnings: skippedCount,
          duration: Date.now() - startTime,
          timestamp: new Date(),
          results: importResults,
        };
        
        setImportReport(report);
        setShowReport(true);
        toast({
          title: 'All Tickets Already Exist',
          description: `${skippedCount} ticket(s) were already imported for ${appointmentDate}`,
        });
        return;
      }
      
      setProgress(`Creating ${ticketsToCreateFiltered.length} new tickets in database (parallel)...`);
      
      const ticketCreationPromises = ticketsToCreateFiltered.map(async (ticketData, index) => {
        try {
          // The vehicle comes from the canonical writer. A year/make/model match is NOT
          // identity (it attached tickets to an unrelated customer's car); the server
          // reuses an existing vehicle only on an exact VIN match within this shop.
          const vehicleId = await createVehicle({
            year: ticketData.year || 0,
            make: ticketData.make || '',
            model: ticketData.model || '',
            engine_size: ticketData.engineSize || null,
            vin: ticketData.vin || null,
            license_plate: null,
            shop_id: shopId,
          });

          // Use VIN from parsed CSV data (avoids reading encrypted field directly)
          const vehicleVin = ticketData.vin || null;

          // Core-100 R3 CORE-Y: the jobs, the ticket and its job links commit TOGETHER through the canonical
          // atomic primitive (create_ticket_bundle) — the CSV path used to insert them as independent
          // statements and never inspected the ticket_jobs results, so a failed link left a ticket with
          // missing work while the import reported success. A row with no jobs is refused (a ticket with no
          // work is exactly the invalid object the primitive exists to prevent), with the reason stated.
          if (!ticketData.jobs || ticketData.jobs.length === 0) {
            throw new Error(`Ticket ${ticketData.ticketNumber}: no jobs — a ticket needs at least one job`);
          }
          const jobRows = ticketData.jobs.map((jobData) => {
            let bayRequirement = (jobData.bay || '').trim();
            const allowedBays = ['Lift', 'Flat', 'Alignment', 'Specialty'];
            if (!allowedBays.includes(bayRequirement)) {
              bayRequirement = 'Flat';
            }
            // SECURITY FIX SEC-003: Use already-verified shopId instead of redundant shop_profile lookup
            return {
              category: jobData.category,
              submenu: jobData.submenu,
              name: jobData.name,
              bay_requirement: bayRequirement,
              skill_level: Math.min(5, Math.max(1, jobData.skill ?? 3)),
              standard_minutes: Math.max(6, Math.round(((jobData.customHours ? Math.round(jobData.customHours * 60) : jobData.minutes) || 6) / 6) * 6),
              catalog_job_id: (jobData as any).catalog_job_id ?? null,
              shop_id: shopId,
            };
          });

          const ticketId = crypto.randomUUID();
          // Core-100 R4 CORE-Y-GAP-005: the row's identity — the database admits it once (tickets_csv_import_row_once).
          const importRowKey = await csvImportRowKey(ticketData, appointmentDate);
          const totalMinutes = ticketData.jobs?.reduce((sum: number, job: any) => sum + job.minutes, 0) || 60;
          const isWaiter = ticketData.isWaiter || ticketData.type === 'Waiter';
          const ticketType = isWaiter ? 'Waiter' : 'Drop-off';

          // CSV-A1: dropped the dead `ticketData.type === 'Scheduled'`
          // predicate. The canonical `tickets.type` enum is
          // `'Waiter' | 'Drop-off'` only (per CSI-S1 audit) — `'Scheduled'`
          // was never a valid type value, so the OR-arm of the prior
          // predicate `(isWaiter || ticketData.type === 'Scheduled')`
          // could never trigger via the dead branch. The intent of the
          // gate was "Waiters get locked scheduled_start_time" (which
          // matches business semantics — Drop-offs don't have promise
          // times). Closes the CST-A2 deferred reference.
          //
          // Parse scheduled time for Waiter appointments.
          let scheduledStartTime: string | null = null;
          if (isWaiter && ticketData.scheduledTime) {
            try {
              // Convert from shop timezone to UTC
              scheduledStartTime = convertDatetimeLocalToUTC(ticketData.scheduledTime, shopTimezone);
            } catch (e) {
              console.error('Failed to parse scheduled time:', ticketData.scheduledTime);
            }
          }

          // LOW-5 C1: Build a proper UTC fallback (06:00 shop-local → UTC ISO, not bare string)
          // The bare `${appointmentDate}T06:00:00` had no timezone suffix and was browser-interpreted
          const fallbackStartUtc = convertDatetimeLocalToUTC(`${appointmentDate}T06:00`, shopTimezone);

          // R32: Clamp to now whenever start is in the past — not just today.
          // Prior version only guarded `appointmentDate === todayShop`, which
          // missed legitimate past-dated CSV rows (legacy timestamps, exports
          // re-imported across days, copy/paste mistakes). For tickets dated
          // tomorrow with a stale 2020 ISO timestamp, the past-time check
          // would have been skipped and the ticket inserted with start_time
          // years in the past. Guard universally: if computedStartTime is
          // before now, snap to now and warn the operator.
          // Chronomancer R-PAST-3: route through `clock` for test-mode
          // determinism. Previously used browser `Date.now()` and
          // `new Date().toISOString()` directly, so a test freezing
          // `clock.setFixed(t)` saw real wall time here and the clamp
          // fired inconsistently. All other past-time guards in the app
          // (useCatalystAutoAssign, cascade, EFS) route through `clock`;
          // ImportJobs was the outlier.
          let computedStartTime = scheduledStartTime || fallbackStartUtc;
          const nowMsImport = clock.nowMs();
          if (new Date(computedStartTime).getTime() < nowMsImport) {
            computedStartTime = new Date(nowMsImport).toISOString();
            toast({
              title: 'Start time adjusted',
              description: `Ticket ${ticketData.ticketNumber}: scheduled start was in the past — set to now.`,
            });
          }

          // SECURITY FIX SEC-003: Use already-verified shopId instead of redundant shop_profile lookup
          const ticketRow = {
            id: ticketId,
            creation_method: 'csv_import', // Core-100 R2 CORE-Y-GAP-001
            simple_ticket_number: 0, // Placeholder - trigger will set real value
            ticket_number: ticketData.ticketNumber, // Store RO number for idempotency
            import_row_key: importRowKey,
            vehicle_id: vehicleId,
            customer_name: ticketData.customerName,
            customer_phone: ticketData.customerPhone || null,
            start_time: computedStartTime,
            scheduled_date: appointmentDate, // CRITICAL: Set scheduled_date for filtering
            scheduled_start_time: scheduledStartTime, // Lock start time for Waiters/Scheduled
            type: ticketType,
            priority: 'Normal',
            // CSV-A1: stop trusting `ticketData.status` from CSV input.
            // Pre-fix this was `status: ticketData.status || 'Active'`,
            // passing the raw CSV value directly into `tickets.status`.
            // Per the canonical 6-status enum (`tickets_status_check`
            // migration 20260424000000: `Active|Paused|Completed|
            // Blocked|Deleted|Cancelled`), any non-canonical CSV value
            // (`'Scheduled'`, `'Pending'`, `'Open'`, `'In Progress'`,
            // arbitrary external-system status text) caused the entire
            // ticket insert to fail with a check-constraint violation,
            // surfacing as an opaque `'failed'` row in the import
            // results. Canonical truth model: `tickets.status` is
            // internal scheduling state maintained by the canonical
            // mutation helpers (`mutateTicketTimerState`, etc.), not
            // CSV import data. Imported tickets always start in
            // canonical `'Active'`; lifecycle transitions (Paused,
            // Completed, etc.) are runtime concerns, not CSV-data
            // concerns. This silently normalizes any non-canonical
            // imported status to `'Active'` — preserves the previously-
            // valid `status: 'Active'` import contract while fixing
            // the bypass surface for non-canonical values.
            status: 'Active',
            predicted_minutes: totalMinutes,
            shop_id: shopId,
            assigned_tech_id: null,
            due_out: null,
            // vehicle_present removed — derived from physical state (Codex §5).
            // VPV-A2: companion CSV verification checkbox + ParsedTicket
            // field also removed; the column is now exclusively trigger-
            // derived (no CSV-side write path).
            is_comeback: ticketData.isComeback ?? false,
            original_tech_id: null, // Will be populated below if comeback
            // Core-100 R4: not auto-assigning → the ticket waits on the backlog, awaiting assignment.
            // OD-40: unassigned until the advisor places it through OptiPlace (the placement review queue).
            parking_lot_reason: CSV_IMPORT_AWAITING_ASSIGNMENT_REASON,
          };

          // Job links with bay snapshots, resolved from the catalog BEFORE anything is written.
          const ticketJobRows = await Promise.all(ticketData.jobs.map(async (jobData) => {
            // Try to match job to catalog by name
            const { data: catalogJob } = await newJobCatalogQuery('id, primary_bay_type, allowed_bay_types, required_capabilities, excluded_bay_types')
              .eq('label', jobData.name)
              .maybeSingle();

            // Fetch shop override if catalog job found
            let override = null;
            if (catalogJob && shopId) {
              const { data: overrideData } = await supabase
                .from('jobs_catalog_overrides')
                .select('primary_bay_type_override, allowed_bay_types_override, required_capabilities_override, excluded_bay_types_override, disabled')
                .eq('shop_id', shopId)
                .eq('job_id', catalogJob.id)
                .maybeSingle();

              override = overrideData;
            }

            // Resolve bay rules (override takes precedence)
            return {
              minutes_override: jobData.customHours ? Math.round(jobData.customHours * 60) : null,
              notes: jobData.notes || null,
              allowed_bay_types_snapshot: override?.allowed_bay_types_override ?? catalogJob?.allowed_bay_types ?? [],
              required_capabilities_snapshot: override?.required_capabilities_override ?? catalogJob?.required_capabilities ?? [],
              excluded_bay_types_snapshot: override?.excluded_bay_types_override ?? catalogJob?.excluded_bay_types ?? [],
              bay_requirement_source: override ? 'manual_override' : 'catalog',
            };
          }));

          // All or nothing: the ticket, its jobs and their links (throws on any failure — nothing is left behind).
          await createTicketBundle([{ ticket: ticketRow, jobs: jobRows, ticketJobs: ticketJobRows }]);

          // OCIS-R70-FIX (HIGH-2): CSV-imported tickets previously wrote no
          // timeline event — every imported ticket appeared on the board
          // with no audit trail for its creation. logTicketCreated dual-
          // writes Pensieve (advisor metrics) + OCIS (timeline).
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user?.id) {
              await logTicketCreated({
                ticketId,
                shopId,
                userId: user.id,
                ticketType,
                totalMinutes,
                shopTimezone,
              });
            }
          } catch (logErr) {
            console.error('[CSV Import] logTicketCreated error:', logErr);
          }

          // If this is a comeback ticket, try to find original_tech_id from prior completed tickets
          if (ticketData.isComeback && (vehicleId || vehicleVin) && shopId) {
            try {
              // By vehicle identity first: the stored VIN is ciphertext, so the
              // by-VIN lookup can only ever match a legacy plaintext row.
              let originalTechId: string | null = null;
              if (vehicleId) {
                const { data } = await supabase.rpc('get_latest_completed_tech_for_vehicle', {
                  p_shop_id: shopId,
                  p_vehicle_id: vehicleId,
                });
                originalTechId = data ?? null;
              }
              if (!originalTechId && vehicleVin) {
                const { data } = await supabase.rpc('get_latest_completed_tech_by_vin', {
                  p_shop_id: shopId,
                  p_vin: vehicleVin
                });
                originalTechId = data ?? null;
              }

              if (originalTechId) {
                await supabase
                  .from('tickets')
                  .update({ original_tech_id: originalTechId })
                  .eq('id', ticketId);
                
                console.log(`[CSV Import] Set original_tech_id ${originalTechId} for comeback ticket ${ticketId}`);
              }
            } catch (e) {
              console.error('[CSV Import] Failed to lookup original tech:', e);
            }
          }

          return { ticketId, ticketData, index, ticketType };
        } catch (error) {
          console.error(`Error creating ticket ${index}:`, error);
          // Core-100 R4 CORE-Y: "an import MUST report what happened to every row" — a refused row is reported with
          // its reason (already imported → warning; anything else → failed), never silently dropped.
          const outcome = classifyImportRowError(error);
          importResults.push({ ticketNumber: ticketData.ticketNumber, status: outcome.status, message: outcome.message });
          return null;
        }
      });

      const createdTickets = (await Promise.all(ticketCreationPromises)).filter(Boolean);

      {
        // OD-40: nothing is assigned here. Every created ticket awaits placement through OptiPlace (the queue below);
        // every row is reported.
        for (const item of createdTickets) {
          if (!item) continue;
          const outcome = awaitingAssignmentOutcome();
          importResults.push({ ticketNumber: item.ticketData.ticketNumber, status: outcome.status, message: outcome.message });
        }
        const report: ImportReportData = {
          totalTickets: tickets.length,
          successful: importResults.filter(r => r.status === 'success').length,
          failed: importResults.filter(r => r.status === 'failed').length,
          warnings: importResults.filter(r => r.status === 'warning').length,
          duration: Date.now() - startTime,
          timestamp: new Date(),
          results: importResults,
        };
        setImportReport(report);
        setShowReport(true);
        toast({
          title: createdTickets.length === 0 ? 'No tickets were created' : 'Import complete — review placements',
          description: createdTickets.length === 0
            ? 'See the report for what happened to each row.'
            : `${createdTickets.length} ticket(s) created. Review each with OptiPlace below, or defer it.`,
          variant: createdTickets.length === 0 ? 'destructive' : undefined,
        });
        if (createdTickets.length > 0) {
          setPlacementRefresh((k) => k + 1);
          setSelectedDayLabel(scheduleDayLabel);
          if (scheduleDayLabel) {
            const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
            await triggerBoardCascade(shopId, scheduleDayLabel);
          }
        }
        return;
      }


    } catch (error: any) {
      console.error('Import error:', error);
      toast({
        title: 'Import failed',
        description: error.message || 'Failed to import tickets',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
      setIsAnalyzing(false);
      setProgress('');
    }
  };

  const downloadTemplate = () => {
    const templateCSV = `ticketNumber,year,make,model,engineSize,customerName,customerPhone,job1,job1_hours,job2,job2_hours,job3,job3_hours,appointmentDate,status
RO-12345,2020,HONDA,Civic,1.5L TURBO,John Doe,555-1234,Oil Change,0.5,Tire Rotation,0.5,,,2024-01-15,Active
RO-12346,2019,TOYOTA,Camry,2.5L,Jane Smith,555-5678,Brake Inspection,1.0,Oil Change,0.5,,,2024-01-15,Active`;
    
    const blob = new Blob([templateCSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      
      <div className="container mx-auto p-6 max-w-4xl">
        <ImportReport
          open={showReport}
          onOpenChange={setShowReport}
          report={importReport}
        />

        {/* OD-40: imported tickets are placed through OptiPlace, one at a time, by the advisor. */}
        <ImportPlacementReviewQueue shopId={activeShop?.id} shopTimezone={shopTimezone} refreshKey={placementRefresh} />
        
        {parsedTickets && (
          <>
            <CSVTicketVerification
              tickets={parsedTickets}
              onConfirm={handleImportClick}
              onCancel={() => {
                setParsedTickets(null);
                setSelectedFile(null);
              }}
            />
            {showCalendar && (
              <CalendarModal
                open={showCalendar}
                onOpenChange={setShowCalendar}
                currentDate={new Date()}
                onDateSelect={handleDateSelect}
              />
            )}
          </>
        )}

        {/* Analyzing Modal */}
        {isAnalyzing && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <Card className="w-full max-w-md mx-4">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <Loader2 className="h-12 w-12 animate-spin text-primary" />
                  <div className="text-center space-y-2">
                    <h3 className="text-lg font-semibold">Analyzing Tickets</h3>
                    <p className="text-sm text-muted-foreground">
                      Running intelligent assignment analysis...
                    </p>
                    {progress && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {progress}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {!parsedTickets && (
          <Card className="border-primary/20">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
              <CardTitle className="flex items-center gap-2 text-2xl">
                <FileSpreadsheet className="h-7 w-7 text-primary" />
                Import Jobs from CSV
              </CardTitle>
              <CardDescription className="text-base">
                Upload a CSV file to automatically create and schedule service tickets with intelligent technician assignment
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6 pt-6">
              <div className="space-y-4">
                {/* Drag and Drop Zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => document.getElementById('csv-file-input')?.click()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-8 transition-all cursor-pointer",
                    isDragging 
                      ? "border-primary bg-primary/5 scale-[1.02]" 
                      : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/5",
                    (isParsing || isImporting) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <div className="flex flex-col items-center justify-center gap-4 text-center">
                    <div className={cn(
                      "rounded-full p-4 transition-colors",
                      isDragging ? "bg-primary/10" : "bg-muted"
                    )}>
                      <Upload className={cn(
                        "h-8 w-8 transition-colors",
                        isDragging ? "text-primary" : "text-muted-foreground"
                      )} />
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-lg font-semibold">
                        {selectedFile ? selectedFile.name : 'Drop your CSV file here'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        or click to browse files
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Maximum file size: 2MB
                      </p>
                    </div>

                    {selectedFile && (
                      <Button
                        size="lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleParse();
                        }}
                        disabled={isParsing || isImporting}
                        className="bg-primary hover:bg-primary-hover"
                      >
                        {isParsing ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Parsing...
                          </>
                        ) : (
                          'Parse CSV'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {progress && (
                  <div className="p-3 bg-muted rounded-lg border border-primary/20">
                    <p className="text-sm font-medium text-foreground">{progress}</p>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={downloadTemplate}
                    className="text-primary hover:text-primary-hover"
                  >
                    Download CSV Template
                  </Button>
                </div>
              </div>

              <div className="bg-accent/5 rounded-lg p-4 border border-accent/20">
                <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent"></span>
                  How It Works
                </h3>
                <ol className="space-y-2 text-sm text-muted-foreground ml-4">
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary">1.</span>
                    <span>Upload your CSV file with ticket information</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary">2.</span>
                    <span>Our parser validates and matches jobs to your catalog</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary">3.</span>
                    <span>Review parsed tickets and select a schedule date</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary">4.</span>
                    <span>AI analyzes and assigns tickets to optimal technicians</span>
                  </li>
                </ol>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ImportJobs;
